import type { BadgeColor } from '@enamelai/design-system'
import { getEnv } from '@utils/env'

/** No backup API yet; enable once "Backup now" can trigger a real backup. */
export const BACKUP_NOW_ENABLED =
  getEnv('REACT_APP_BACKUP_NOW_ENABLED', 'false') === 'true'

export type BackupFrequency = 'daily' | 'weekly' | 'monthly'

export type RetentionUnit = 'days' | 'weeks' | 'months'

export interface BackupSettingsForm {
  allowBackup: boolean
  backupTime: string
  retentionPeriod: string
  retentionUnit: RetentionUnit
  backupFrequency: BackupFrequency
  useEncryption: boolean
  sendNotificationOnFailed: boolean
  verifyBeforeBackup: boolean
}

export type BackupHistoryStatus = 'backup' | 'manual' | 'failed'

export type BackupHistoryRow = {
  id: string
  date: string
  size: string
  status: BackupHistoryStatus
}

export const BACKUP_STATUS_BADGES: Record<
  BackupHistoryStatus,
  { label: string; color: BadgeColor }
> = {
  backup: { label: 'Backup', color: 'vivid-green' },
  manual: { label: 'Manual backup', color: 'vivid-green' },
  failed: { label: 'Failed', color: 'red' },
}

const formatHourLabel = (hour24: number): string => {
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${String(hour12).padStart(2, '0')}:00 ${period}`
}

export const BACKUP_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: formatHourLabel(hour),
  label: formatHourLabel(hour),
}))

export const RETENTION_UNIT_OPTIONS: { value: RetentionUnit; label: string }[] =
  [
    { value: 'days', label: 'days' },
    { value: 'weeks', label: 'weeks' },
    { value: 'months', label: 'months' },
  ]

export const BACKUP_FREQUENCY_OPTIONS: {
  value: BackupFrequency
  label: string
}[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

export const BACKUP_TOGGLES: {
  key: 'verifyBeforeBackup' | 'sendNotificationOnFailed' | 'useEncryption'
  label: string
}[] = [
  { key: 'verifyBeforeBackup', label: 'Verify backup integrity' },
  { key: 'useEncryption', label: 'Encrypt backups' },
  {
    key: 'sendNotificationOnFailed',
    label: 'Notify on failed or missed backups',
  },
]

export const DEFAULT_BACKUP_SETTINGS: BackupSettingsForm = {
  allowBackup: true,
  backupTime: '05:00 AM',
  retentionPeriod: '30',
  retentionUnit: 'days',
  backupFrequency: 'daily',
  useEncryption: true,
  sendNotificationOnFailed: true,
  verifyBeforeBackup: true,
}
